import { withAnalyticsClient, GenericAnalyticsClient } from '@acorns/web-utils'
import { withToggles, WithTogglesProps } from '@acorns/web-toggles-react'
import PropTypes from 'prop-types'
import React, { PureComponent, ComponentType } from 'react'
import { withRouter } from 'react-router-dom'
import { compose, getContext } from 'recompose'

import { VISITED_PAGES, ACORNS_HELLO_FLOW } from 'app-constants'
import { updateThemeColor } from 'theme'
import CenteredSpinner from 'components/CenteredSpinner'
import { translateEventProps } from 'utils/analytics'

import flowMap from './flows'
import {
  FlowEntry,
  FlowProgressWithSteps,
  FlowProviderProps,
  FlowProviderRedirectConfig,
  FlowRoute,
  FlowProviderRedirectConfig as RedirectConfig,
  FlowProviderState as State,
  Flow,
} from './types'
import { getFlow, getVisitedPages, navStepMap } from './utils'

export const IS_REGISTRATION_FLOW = 'IS_REGISTRATION_FLOW'

interface WithAnalyticsClientProps {
  analytics: GenericAnalyticsClient
}

export type Props = FlowProviderProps & WithAnalyticsClientProps & WithTogglesProps

// This is the provider that provides the redirect logic
class Provider extends PureComponent<Props, State> {
  public static childContextTypes = {
    route: PropTypes.func.isRequired,
    flow: PropTypes.any.isRequired,
    visitedPages: PropTypes.array.isRequired,
    flowProgress: PropTypes.func.isRequired,
    setFlow: PropTypes.func.isRequired,
    isRegistrationFlow: PropTypes.bool.isRequired,
    routeIfInvalidPage: PropTypes.func.isRequired,
    retrieveRouteDestination: PropTypes.func.isRequired,
  }

  protected flow: Flow

  /**
   * Flag indicating if user is still in registration funnel or came from app experience
   */
  public readonly isRegistrationFlow: boolean

  constructor(props) {
    super(props)
    this.state = { userState: {} }
    this.flow = getFlow()

    const queryParams = new URLSearchParams(window.location.search)
    this.isRegistrationFlow = !queryParams.get('flow')
    window.sessionStorage.setItem(IS_REGISTRATION_FLOW, String(this.isRegistrationFlow))
  }

  public getChildContext() {
    const {
      route,
      flow,
      flowProgress,
      setFlow,
      isRegistrationFlow,
      routeIfInvalidPage,
      retrieveRouteDestination,
    } = this

    return {
      route,
      flow,
      visitedPages: getVisitedPages(),
      flowProgress,
      setFlow,
      isRegistrationFlow,
      routeIfInvalidPage,
      retrieveRouteDestination,
    }
  }

  public setFlow = (flow) => {
    window.localStorage.setItem(ACORNS_HELLO_FLOW, flow)
    this.flow = getFlow()
  }

  public retrieveRouteDestination = (config: RedirectConfig): FlowRoute => {
    const entries = flowMap[this.flow]
    const entry = entries && entries.find((e) => e.page === (config.from || location.pathname))
    const action = entry && entry.actions.find((a) => a.name === config.action)
    return action.goTo(config.state, { ...this.props, isAnalytics: true })
  }

  // Looks for the flow entries, then finds the corresponding entry based on the current page + user action.
  // Then executes the resolver function to know where to go next and redirect
  // It also tracks the step if necessary
  public route = (config: RedirectConfig) => {
    // ToDo: This could be on the browser storage for persistance
    const userState = { ...this.state.userState, ...config.state }
    this.setState({ userState })

    const entries = flowMap[this.flow]
    // If we're unable to specify a `from` (because we're redirecting from a mixin component)
    // we fall back to the current pathname
    const entry = entries && entries.find((e) => e.page === (config.from || location.pathname))
    const action = entry && entry.actions.find((a) => a.name === config.action)

    let path =
      action &&
      action.goTo(userState, {
        visitedPages: getVisitedPages(),
        ...this.props,
        setFlow: this.setFlow,
        actionName: config.action,
        isRegistrationFlow: this.isRegistrationFlow,
      })

    path = path || config.path // Use user override if !path

    if (!path) return

    if (this.flowJustStarted(config.from)) {
      this.clearVisitedPages()
    }

    if (!config.doNotTrackVisited) {
      this.addVisitedPage(path)
      config.locationState
        ? (config.locationState['referrer'] = entry.page)
        : (config.locationState = { referrer: entry.page })
      this.props.analytics.track(
        'Route Attempt',
        translateEventProps({
          from: config.from,
          action: config.action,
          referrer: entry.page,
          to: path,
        }),
      )
    }

    this.props.history.push(path, config.locationState && config.locationState)
    updateThemeColor()
  }

  public flowProgress: (path: string) => FlowProgressWithSteps = (path) => {
    const entries = flowMap[this.flow]
    const isMultiplePagesSuitabilityQuestions = path.includes(FlowRoute.SuitabilityQuestionsRoot)
    const entry =
      entries &&
      entries.find(
        (e) =>
          e.page === path ||
          // The Suitability Questions adds multiple pages but should be considered as one step in the progress here
          (isMultiplePagesSuitabilityQuestions &&
            e.page?.includes?.(FlowRoute.SuitabilityQuestionsRoot)),
      )

    return this.configureFlowProgress(entry)
  }

  public render() {
    if (!this.flow) {
      return <CenteredSpinner />
    }
    return <>{this.props.children}</>
  }

  public routeIfInvalidPage = () => {
    const entries = flowMap[this.flow]
    const currentPage = location.pathname
    const doesFlowHavePage = entries.some(
      (entry) =>
        currentPage === entry.page ||
        (entry.page.includes(FlowRoute.SuitabilityQuestionsRoot) &&
          currentPage.includes(FlowRoute.SuitabilityQuestionsRoot)),
    )

    if (doesFlowHavePage) return
    const firstPage = entries.find((entry) => entry.isFirstPage).page
    this.props.history.push(firstPage)
  }

  private configureFlowProgress: (entry: FlowEntry) => FlowProgressWithSteps = (entry) => {
    return navStepMap.get(this.flow)?.(entry, { visitedPages: getVisitedPages(), ...this.props })
  }

  private addVisitedPage = (page: string) => {
    const visitedPages = getVisitedPages()
    visitedPages.push(page)
    window.localStorage.setItem(VISITED_PAGES, JSON.stringify(visitedPages))
  }

  private clearVisitedPages = () => {
    window.localStorage.removeItem(VISITED_PAGES)
  }

  private flowJustStarted = (from: string) => {
    return from === FlowRoute.Welcome || from === FlowRoute.Signup
  }
}

const FlowProvider = compose(withRouter, withAnalyticsClient, withToggles)(Provider)

// This is the HOC for using on the enhanced components.
export const withFlowRouter = <T extends object>(Component: ComponentType<T>) => {
  return compose<WithFlowRouterProps, T>(
    getContext({
      route: PropTypes.func,
      flow: PropTypes.string,
      visitedPages: PropTypes.array,
      flowProgress: PropTypes.func,
      setFlow: PropTypes.func,
      isRegistrationFlow: PropTypes.bool,
      routeIfInvalidPage: PropTypes.func,
      retrieveRouteDestination: PropTypes.func,
    }),
  )(Component as any)
}

export type WithFlowRouterProps = {
  route: (config: FlowProviderRedirectConfig) => void
  flow: Flow
  visitedPages: string[]
  flowProgress: (path: string) => FlowProgressWithSteps
  setFlow: (flow: Flow) => void
  isRegistrationFlow: boolean
  routeIfInvalidPage: () => void
  retrieveRouteDestination: (config: FlowProviderRedirectConfig) => FlowRoute
}

export default FlowProvider
